import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { differenceInDays, subDays } from 'date-fns';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import { ChartLoadingGuard } from 'components/ChartLoadingGuard';
import { useConfirm } from 'components/ConfirmDialog';
import {
  useWeatherAlert,
  SaveAlertData,
} from 'features/weatherAlerts/hooks/useWeatherAlert';
import { WeatherAlert } from 'features/weatherAlerts/weatherAlerts.types';
import { WaterLevel, WaterLevelObservation } from '../../waterLevel.types';
import { useFormBlocker } from '../../../../hooks/useFormBlocker';
import { useWaterLevelObservations } from '../../hooks/useWaterLevelObservations';
import { CategoryChips } from '../CategoryChips';
import { useWaterLevelsChart } from '../../hooks/useWaterLevelsChart';
import { EchartsWrapper } from '../../../../libs/echarts/EchartsWrapper';

type WaterLevelAlertFormProps = {
  waterLevel: WaterLevel;
  alert?: WeatherAlert;
  fromRoute: string;
};

type FormValues = {
  height?: number | string;
};

// Full-bleed chart compensation values
const CHART_FULL_BLEED_MARGIN_LEFT = -2; // -16px to offset container gap
const CHART_FULL_BLEED_WIDTH_EXTENSION = 4; // +32px to extend to edges

const getHeightFieldSlotProps = (
  value: FormValues['height'],
  unit: string,
): TextFieldProps['slotProps'] => {
  const base: TextFieldProps['slotProps'] = {
    htmlInput: {
      inputMode: 'decimal',
      maxLength: 7,
    },
    input: {
      endAdornment: value ? (
        <InputAdornment position="end">{unit}</InputAdornment>
      ) : null,
    },
  };

  if (Capacitor.getPlatform() !== 'ios') return base;

  // iOS/WKWebView doesn't reliably fire blur events, leaving MUI's FormControl
  // stuck in focused=true and the notch open. Tying notched to the value
  // directly sidesteps this. Can't be a theme defaultProp (needs runtime value)
  // or styleOverrides (notched is structural, not CSS; ownerState doesn't carry
  // the live value).
  return {
    ...base,
    input: {
      ...base.input,
      notched: Boolean(value),
    },
    inputLabel: {
      sx: {
        // Masks the border behind the floating label. Uses the CSS variable
        // rather than an sx token so it resolves correctly in both color modes.
        '&.MuiInputLabel-shrink': {
          backgroundColor: 'var(--mui-palette-background-paper)',
          paddingInline: '4px',
          marginInlineStart: '-4px',
        },
      },
    },
  };
};

const getNumberOfDaysReturned = (
  observations: WaterLevelObservation[],
): number => {
  const firstObservation = observations.at(-1);

  if (!firstObservation) {
    return 1;
  }

  return differenceInDays(new Date(), firstObservation.timestamp);
};

export const WaterLevelAlertForm = (
  props: WaterLevelAlertFormProps,
): JSX.Element => {
  const { waterLevel, alert, fromRoute } = props;
  const observationsHistoryStart = subDays(new Date(), 365);
  const {
    observations,
    isLoading: isLoadingObservations,
    error: observationsError,
  } = useWaterLevelObservations({
    id: waterLevel.id,
    start: observationsHistoryStart,
    end: new Date(),
  });
  const { t } = useTranslation();
  const { saveAlert, deleteAlert } = useWeatherAlert({ alertId: alert?.id });
  const confirm = useConfirm();

  const { control, formState, handleSubmit } = useForm<FormValues>({
    resolver: yupResolver(
      Yup.object().shape({
        height: Yup.number()
          .typeError(t('waterLevel.alerts.inputs.height.required'))
          .min(-1000, t('waterLevel.alerts.inputs.height.min'))
          .max(50000, t('waterLevel.alerts.inputs.height.max'))
          .required(t('waterLevel.alerts.inputs.height.required')),
      }),
    ),
    values: {
      height: alert?.criteria[0].thresholdValue || '',
    },
  });

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.dirtyFields.height),
  });

  const waterLevelsChartOptions = useWaterLevelsChart({
    observations: observations || [],
    waterLevel,
  });

  const navigateBack = (): void => {
    unblockedNavigate(fromRoute);
  };

  const handleDeleteAlert = async (): Promise<void> => {
    if (!alert) {
      return;
    }

    const resp = await confirm(t('waterLevel.alerts.deleteConfirmation'), {
      title: t('waterLevel.alerts.deleteNotification'),
    });
    if (!resp) {
      return;
    }

    try {
      await deleteAlert();
      navigateBack();
    } catch (error) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
    }
  };

  const handleSaveAlert: SubmitHandler<FormValues> = async (values) => {
    const level =
      typeof values.height === 'string'
        ? parseFloat(values.height)
        : values.height || 0;

    const data: SaveAlertData = {
      id: alert?.id,
      stationId: waterLevel.id,
      criteria: [
        {
          fieldName: 'level',
          operator: 'gte',
          thresholdValue: level,
        },
      ],
    };

    try {
      await saveAlert(data);
      navigateBack();
    } catch (finalError) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error: finalError });
    }
  };

  const actionText = alert
    ? t('waterLevel.alerts.saveNotification')
    : t('waterLevel.alerts.addNotification');

  return (
    <Stack spacing={2}>
      <Typography variant="h2">{waterLevel?.name}</Typography>

      <ChartLoadingGuard
        isLoading={isLoadingObservations}
        error={Boolean(observationsError)}
      >
        {waterLevelsChartOptions && observations && (
          <>
            <Typography component="h3" sx={{ fontWeight: 'bold' }}>
              {t('waterLevel.alerts.lastNDays', {
                days: getNumberOfDaysReturned(observations),
              })}
            </Typography>

            <Box
              sx={(theme) => ({
                // Full bleed: extend chart to panel edges
                // Compensates for container padding/gaps to achieve full-width chart
                marginLeft: `${theme.spacing(CHART_FULL_BLEED_MARGIN_LEFT)} !important`,
                width: `calc(100% + ${theme.spacing(CHART_FULL_BLEED_WIDTH_EXTENSION)})`,
              })}
            >
              <EchartsWrapper option={waterLevelsChartOptions} />
            </Box>

            <CategoryChips
              categoryLevels={waterLevel.floodCategories}
              units={waterLevel.units.level}
            />
          </>
        )}
      </ChartLoadingGuard>

      <Typography>{t('waterLevel.alerts.section')}</Typography>

      <Controller
        control={control}
        name="height"
        render={({ field, fieldState }) => {
          const { ref, value, ...muiFieldProps } = field;
          return (
            <TextField
              label={t('waterLevel.alerts.inputs.height.label')}
              fullWidth
              {...muiFieldProps}
              value={value}
              inputRef={ref}
              error={Boolean(fieldState.error)}
              helperText={
                fieldState.error?.message ||
                t('waterLevel.alerts.inputs.height.helperText')
              }
              slotProps={getHeightFieldSlotProps(value, waterLevel.units.level)}
            />
          );
        }}
      />

      {alert && (
        <Button
          fullWidth
          size="large"
          disabled={formState.isSubmitting}
          onClick={handleDeleteAlert}
          color="error"
        >
          {t('waterLevel.alerts.deleteNotification')}
        </Button>
      )}

      <Button
        fullWidth
        size="large"
        disabled={formState.isSubmitting}
        onClick={handleSubmit(handleSaveAlert)}
      >
        {actionText}
      </Button>
    </Stack>
  );
};
